import i18n from 'app-i18n';
import { DateTime } from 'luxon';
import { menuDisplayInfoSchema } from 'util/menu-display-info-schema';
import { array, boolean, mixed, number, object, string } from 'yup';

const groupDisplayInfoSchema = array().of(
  object().shape({
    langCode: string(),
    title: string(),
    shortDescription: string().nullable(),
    longDescription: string().nullable(),
    smallImage: array().of(string().nullable()),
    mediumImage: array().of(string().nullable()),
  }),
);

const groupItemSettingsSchema = object().shape({
  itemId: string().nullable(),
  itemGuid: string().nullable(),
  maxItemSelections: number().nullable(),
  price: number().nullable(),
  isDefault: boolean().nullable(),
  displayRank: number().nullable(),
});

const groupsSchema = object().shape({
  clientId: string().nullable(),
  merchantId: string().nullable(),
  externalId: string().nullable(),
  title: string().nullable(),
  createdDate: string().nullable(),
  updatedDate: string().nullable(),
  startDate: string().default(DateTime.now().toISODate()).nullable(),
  endDate: string().nullable(),
  displayRank: number().nullable(),
  displayInfo: groupDisplayInfoSchema,
  status: string().required(),
  applicableOrderTypes: array().of(string()).nullable(),
  extendedAttributes: array().default([]).nullable(),
  privateAttributes: array().default([]).nullable(),
  storeIds: array().of(string()).nullable(),
  groupId: string().nullable(),
  groupType: string().nullable(),
  itemIds: array().of(string()).nullable(),
  defaultSelectedItems: array().default([]).nullable(),
  mandatoryItems: array().default([]).nullable(),
  optionGroupIds: array().of(string()).nullable(),
  subGroupIds: array().default([]).nullable(),
  selectionMandatory: boolean().nullable(),
  inheritParentOptions: boolean().nullable(),
  maxItemSelections: number().nullable(),
  minItemSelections: number().nullable(),
  groupItemSettings: array().of(groupItemSettingsSchema).nullable(),
});

const storeSchema = object().shape({
  title: string().required(),
  storeId: string().required(),
  smallImage: array().of(string()).nullable(),
});

export const menusFormSchema = object().shape({
  clientId: string(),
  merchantId: string(),
  title: string()
    .required()
    .default('')
    .when('externalId', {
      is: externalId => !externalId,
      then: string().max(100),
      otherwise: string().nullable(),
    }),
  originalTitle: string().nullable(),
  externalId: string().nullable(),
  createdDate: string(),
  updatedDate: string(),
  startDate: string()
    .when('$isEdit', (isEdit, schema) =>
      schema.test({
        name: 'startDate',
        exclusive: false,
        message: i18n.t('forms.menusForm.basicInfo.startDate.validation'),
        test: startDate =>
          isEdit
            ? true
            : DateTime.fromISO(startDate) >= DateTime.now().minus({ day: 1 }),
      }),
    )
    .default(DateTime.now().toISODate()),
  endDate: string().when('startDate', (startDate, schema) =>
    schema.test({
      name: 'endDate',
      exclusive: false,
      message: i18n.t('forms.menusForm.basicInfo.endDate.validation'),
      test: endDate => DateTime.fromISO(endDate) >= DateTime.fromISO(startDate),
    }),
  ),
  displayRank: number().min(0).default(1).required(),
  displayInfo: menuDisplayInfoSchema,
  status: mixed()
    .oneOf(['Active', 'Inactive'])
    .required()
    .default('Active')
    .transform((_value, originalValue) =>
      !originalValue || originalValue === 'Inactive' ? 'Inactive' : 'Active',
    ),
  applicableOrderTypes: array().of(string()).nullable(),
  extendedAttributes: array().default([]),
  privateAttributes: array().default([]),
  storeIds: array().of(string()),
  storeInfos: array()
    .of(storeSchema)
    .default([])
    .when('externalId', {
      is: externalId => !externalId,
      then: array().nullable().min(1).required(),
      otherwise: array().nullable(),
    }),
  catalogId: string(),
  groups: array().of(groupsSchema).default([]),
  groupIds: array().of(string()).nullable(),
});
